import React from 'react'
import {
  CalendarDays,
  CameraOff,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Copy,
  Download,
  ExternalLink,
  FileDown,
  Link as LinkIcon,
  ListFilter,
  Mail,
  Maximize,
  MoreHorizontal,
  Play,
  RotateCcw,
  Search,
  Settings,
  Share2,
  Sparkles,
  Star,
  Trash2,
  UserRound,
  Video,
  Volume2,
} from 'lucide-react'
import ClientDashboardV3Layout from './ClientDashboardV3Layout'
import CurrentClientProvider from '../../../providers/CurrentClientProvider'
import { trackEvent } from '../../../services/AmplitudeService'
import MeetingsService, { type CalendarEvent } from '../../../services/MeetingsService'
import { requestOrbitIndiePanelOpen } from '../../../services/orbit/orbitPageAssistantBridge'
import {
  type Meeting,
  type MeetingActionItem,
  type MeetingAdvisor,
  type MeetingTranscriptLine,
} from './components/meetingsFixtures'
import { cn } from '../../../lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '../../../tailwindComponents/ui/avatar'
import { Button } from '../../../tailwindComponents/ui/button'
import { Card, CardContent } from '../../../tailwindComponents/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../tailwindComponents/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../tailwindComponents/ui/dropdown-menu'
import { Input } from '../../../tailwindComponents/ui/input'
import { Checkbox } from '../../../tailwindComponents/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../tailwindComponents/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../tailwindComponents/ui/select'
import { Skeleton } from '../../../tailwindComponents/ui/skeleton'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../tailwindComponents/ui/tabs'

const MEETINGS_EVENT = 'V3 Meetings Event'

// An "M1 Event" for the calendar's green markers, from
// GET /api/client-dashboard-notifications/calendar (MeetingsService.getCalendarEvents).
// Client-scoped (global + this client) and INCLUDES past/out-of-window + non-active events.
// The register/CTA link is embedded in `body`. Alias of the service's CalendarEvent type.
type M1Event = CalendarEvent

// Temporary UI hides (easy to re-enable). Set back to true to restore the feature.
// - Action-item "Add to dashboard" (accept) + "Dismiss" controls in the meeting detail.
// - The per-meeting "Notes" tab (and its panel) in the meeting detail.
const SHOW_ACTION_ACCEPT_DISMISS = false
const SHOW_NOTES_TAB = false

type HistoryTab = 'all' | 'starred' | 'new'

// Advanced filter state for the Call-history toolbar "Filter" popover. Composes with
// the search box, the All/Starred/New tabs, and the topic chips.
interface HistoryFilters {
  hasRecording: boolean
  hasOpenActions: boolean
  dateFrom: string // yyyy-mm-dd (empty = unbounded)
  dateTo: string // yyyy-mm-dd (empty = unbounded)
}

const EMPTY_FILTERS: HistoryFilters = {
  hasRecording: false,
  hasOpenActions: false,
  dateFrom: '',
  dateTo: '',
}

function activeFilterCount(filters: HistoryFilters, activeTopic: string | null): number {
  let count = 0
  if (filters.hasRecording) count += 1
  if (filters.hasOpenActions) count += 1
  if (filters.dateFrom || filters.dateTo) count += 1
  if (activeTopic) count += 1
  return count
}

// ---- helpers --------------------------------------------------------------

function formatMeetingDate(iso?: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatNextCallDate(iso?: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
}

function initialsFor(name?: string | null): string {
  if (!name) return '?'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

function openActionCount(items?: MeetingActionItem[]): number {
  return (items || []).filter((item) => item.status !== 'done' && !item.suggested).length
}

// SPS-5005 — HubSpot scheduling embed (replaces the old Google Calendar deep link).
// Scheduling / "Open calendar" / "Reschedule" all open the client's per-client HubSpot
// scheduling page (fetched via MeetingsService.getHubspotCalendarUrl) inside an in-page
// modal (iframe). There is no per-meeting reschedule URL, so Reschedule uses the same page.
//
// Builds the iframe src from the per-client scheduling URL, appending ?embed=true (idempotent).
// Returns '' when no scheduling URL is configured — callers then show a "not configured" state.
function buildHubspotEmbedUrl(schedulingUrl?: string | null): string {
  const base = (schedulingUrl || '').trim()
  if (!base) return ''
  if (/[?&]embed=/.test(base)) return base
  return base + (base.includes('?') ? '&' : '?') + 'embed=true'
}

// Shared in-page modal that renders a HubSpot meetings/calendar embed in an iframe. When no
// embed URL is configured yet it shows a clear "not configured" state rather than a blank frame.
function HubSpotCalendarModal({
  open,
  onOpenChange,
  title,
  embedUrl,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  embedUrl: string
}) {
  const url = (embedUrl || '').trim()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-hidden p-0">
        <DialogHeader className="border-b border-[#EAECF0] px-5 py-4">
          <DialogTitle className="text-base font-bold text-[#101828]">{title}</DialogTitle>
        </DialogHeader>
        {url ? (
          <iframe title={title} src={url} className="h-[70vh] w-full border-0" />
        ) : (
          <div className="flex h-[300px] flex-col items-center justify-center gap-2 px-6 text-center">
            <CalendarDays className="h-6 w-6 text-[#98A2B3]" strokeWidth={1.6} />
            <div className="text-sm font-semibold text-[#344054]">Scheduling not available</div>
            <p className="max-w-sm text-xs text-[#667085]">
              This client doesn’t have a HubSpot scheduling page configured yet, so there’s
              nothing to show here. Once a scheduling URL is set for the advisor it will appear
              automatically.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// SPS-5005: export is CSV metadata only for the local test; transcript-content export is
// gated on the data-use sign-off (SPS-5500); PDF deferred.
function csvCell(value: string | number | null | undefined): string {
  const text = String(value ?? '')
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function buildCallHistoryCsv(meetings: Meeting[]): string {
  const header = ['Date', 'Title', 'Topic', 'Duration', 'Attendees', 'Has recording', 'Open actions']
  const rows = meetings.map((meeting) => [
    meeting.dateLabel || formatMeetingDate(meeting.date),
    meeting.title || '',
    (meeting.topics || []).join('; '),
    meeting.duration || '',
    (meeting.attendees || []).map((a) => a.name).join('; '),
    meeting.hasRecording ? 'Yes' : 'No',
    openActionCount(meeting.actionItems),
  ])
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')
}

function downloadCallHistoryCsv(meetings: Meeting[], clientCode: string) {
  const blob = new Blob([buildCallHistoryCsv(meetings)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${(clientCode || 'client').toLowerCase()}-call-history.csv`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// ---- amber / pill primitives ---------------------------------------------

function AmberPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[#FEDF89] bg-[#FFFAEB] px-2.5 py-0.5 text-[11px] font-semibold text-[#B54708]">
      {children}
    </span>
  )
}

function AttendeeStack({ attendees }: { attendees: Meeting['attendees'] }) {
  const shown = (attendees || []).slice(0, 3)
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((attendee, index) => (
        <span
          key={`${attendee.name}-${index}`}
          className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-white text-[8px] font-bold text-white"
          style={{ backgroundColor: attendee.color || '#7F56D9' }}
        >
          {attendee.initials || initialsFor(attendee.name)}
        </span>
      ))}
    </div>
  )
}

// Attendee list — always shows EVERY attendee inline (mini-avatar + name), wrapping as
// needed. No collapse/expand; the full "who attended" list is visible at all times.
function AttendeeList({
  attendees,
  duration,
}: {
  attendees: Meeting['attendees']
  duration?: string | null
}) {
  const all = attendees || []

  return (
    <div className="min-w-0">
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {all.map((attendee, index) => (
          <li
            key={`${attendee.name}-${index}`}
            className="flex items-center gap-1.5 text-[11px] text-[#475467]"
          >
            <span
              className="flex h-4 w-4 items-center justify-center rounded-full text-[7px] font-bold text-white"
              style={{ backgroundColor: attendee.color || '#7F56D9' }}
            >
              {attendee.initials || initialsFor(attendee.name)}
            </span>
            {attendee.name}
          </li>
        ))}
      </ul>
      {duration ? <div className="mt-1 text-[11px] text-[#98A2B3]">{duration}</div> : null}
    </div>
  )
}

// ---- page ----------------------------------------------------------------

export default function ClientDashboardV3Meetings() {
  const currentClientContext = CurrentClientProvider.useClient() as any
  const currentClient = currentClientContext?.client
  const resolvedClientCode = currentClient?.ClientCode || ''
  const resolvedClientId = currentClient?.Id ?? currentClient?.id ?? ''
  const resolvedClientName = currentClient?.Name || currentClient?.ClientName || ''

  const [advisor, setAdvisor] = React.useState<MeetingAdvisor | null>(null)
  const [upcoming, setUpcoming] = React.useState<Meeting[]>([])
  const [past, setPast] = React.useState<Meeting[]>([])
  const [m1Events, setM1Events] = React.useState<M1Event[]>([])
  const [hubspotSchedulingUrl, setHubspotSchedulingUrl] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  const [historyTab, setHistoryTab] = React.useState<HistoryTab>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [activeTopic, setActiveTopic] = React.useState<string | null>(null)
  const [filters, setFilters] = React.useState<HistoryFilters>(EMPTY_FILTERS)

  // SPS-5030: Delete is soft "remove from my view" only — real version persists via a
  // backend hide endpoint; never hard-delete / never touch Grain. These ids are held in
  // LOCAL component state and reset on reload, which is expected for the local test.
  const [hiddenMeetingIds, setHiddenMeetingIds] = React.useState<Set<string>>(() => new Set())
  const handleHideMeeting = React.useCallback(
    (meetingId: string) => {
      setHiddenMeetingIds((prev) => {
        const next = new Set(prev)
        next.add(meetingId)
        return next
      })
      // Persist the soft-hide server-side (fire-and-forget).
      if (resolvedClientId) void MeetingsService.setHidden(resolvedClientId, meetingId, true)
      trackMeetingsInteraction('meeting_hidden', { meetingId })
    },
    // trackMeetingsInteraction is defined below; stable via its own useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedClientId],
  )
  const handleUndoHideMeeting = React.useCallback((meetingId: string) => {
    setHiddenMeetingIds((prev) => {
      if (!prev.has(meetingId)) return prev
      const next = new Set(prev)
      next.delete(meetingId)
      return next
    })
    if (resolvedClientId) void MeetingsService.setHidden(resolvedClientId, meetingId, false)
  }, [resolvedClientId])

  const trackMeetingsInteraction = React.useCallback(
    (action: string, payload: Record<string, unknown> = {}) => {
      trackEvent(MEETINGS_EVENT, {
        clientCode: resolvedClientCode || 'none',
        action,
        ...payload,
      })
    },
    [resolvedClientCode],
  )

  React.useEffect(() => {
    if (!resolvedClientId) {
      setAdvisor(null)
      setUpcoming([])
      setPast([])
      setM1Events([])
      setHubspotSchedulingUrl(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    Promise.all([
      MeetingsService.getAdvisor(resolvedClientId, resolvedClientCode),
      MeetingsService.getMeetings(resolvedClientId, { scope: 'upcoming', clientCode: resolvedClientCode }),
      MeetingsService.getMeetings(resolvedClientId, { scope: 'past', clientCode: resolvedClientCode }),
      // M1 Events (client-scoped: all-clients + this client, incl. past/out-of-window/drafts)
      // for the calendar's green dots.
      MeetingsService.getCalendarEvents(resolvedClientId),
      // Per-client HubSpot scheduling URL for the Schedule/Reschedule/Open-calendar embeds.
      MeetingsService.getHubspotCalendarUrl(resolvedClientId),
    ])
      .then(([advisorData, upcomingData, pastData, eventData, schedulingUrl]) => {
        if (cancelled) return
        setAdvisor(advisorData)
        setUpcoming(Array.isArray(upcomingData) ? upcomingData : [])
        setPast(Array.isArray(pastData) ? pastData : [])
        setM1Events(Array.isArray(eventData) ? eventData : [])
        setHubspotSchedulingUrl(typeof schedulingUrl === 'string' ? schedulingUrl : null)
      })
      .catch((error) => {
        if (!cancelled) console.error('Failed to load meetings hub data', error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [resolvedClientId, resolvedClientCode])

  React.useEffect(() => {
    if (!loading && resolvedClientCode) {
      trackMeetingsInteraction('Page Viewed')
    }
    // Fire once per client after the initial load resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, resolvedClientCode])

  const nextCall = React.useMemo(
    () => upcoming.find((meeting) => meeting.scope === 'next') || upcoming[0] || null,
    [upcoming],
  )
  const upcomingList = React.useMemo(
    () => upcoming.filter((meeting) => meeting.scope === 'upcoming'),
    [upcoming],
  )
  // Rule A: only render the separate "Upcoming calls" list when there are 2+ upcoming
  // meetings total; with a single upcoming meeting we show just the Next call card.
  const showUpcomingList = upcoming.length >= 2

  // Soft-hidden meetings drop out of the list AND every count so the view stays
  // internally consistent after a local "Delete". Full history is shown (no date window).
  const visiblePast = React.useMemo(
    () => past.filter((m) => !hiddenMeetingIds.has(m.id)),
    [past, hiddenMeetingIds],
  )

  const starredCount = React.useMemo(() => visiblePast.filter((m) => m.starred).length, [visiblePast])
  const newCount = React.useMemo(() => visiblePast.filter((m) => m.isNew).length, [visiblePast])
  // Real interaction count — the client's actual past calls, minus any they've hidden.
  const allCount = Math.max(0, past.length - hiddenMeetingIds.size)

  // "since <month year>" derived from the earliest actual call (not a static label).
  const sinceLabel = React.useMemo(() => {
    const times = past
      .map((m) => new Date(m.date as string).getTime())
      .filter((t) => !Number.isNaN(t))
    if (!times.length) return null
    return new Date(Math.min(...times)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }, [past])

  // SPS-5005: topic taxonomy is a product decision (Grain meeting_type vs the design's
  // fixed categories); local build derives chips from the actual data — the distinct
  // topics present on this client's loaded meetings, sorted. "All" is prepended in the UI.
  const topicOptions = React.useMemo(() => {
    const set = new Set<string>()
    visiblePast.forEach((meeting) => (meeting.topics || []).forEach((topic) => set.add(topic)))
    return Array.from(set).sort()
  }, [visiblePast])

  // Real meeting dates for the mini-calendar dots: past calls (orange) + upcoming (purple).
  // M1 Events (green) are layered on separately via the `events` prop.
  const calendarMeetings = React.useMemo(
    () => [...upcoming, ...visiblePast],
    [upcoming, visiblePast],
  )

  const filteredPast = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const fromTs = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).getTime() : null
    const toTs = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`).getTime() : null
    return visiblePast.filter((meeting) => {
      if (historyTab === 'starred' && !meeting.starred) return false
      if (historyTab === 'new' && !meeting.isNew) return false
      if (activeTopic && !(meeting.topics || []).includes(activeTopic)) return false
      if (filters.hasRecording && !meeting.hasRecording) return false
      if (filters.hasOpenActions && openActionCount(meeting.actionItems) === 0) return false
      if (fromTs || toTs) {
        const ts = new Date(meeting.date).getTime()
        if (!Number.isNaN(ts)) {
          if (fromTs && ts < fromTs) return false
          if (toTs && ts > toTs) return false
        }
      }
      if (term) {
        const haystack = [
          meeting.title,
          meeting.summary,
          meeting.typeLabel,
          meeting.notes,
          ...(meeting.topics || []),
          ...(meeting.keyTakeaways || []),
          ...(meeting.keyMoments || []).map((km) => `${km.title} ${km.quote}`),
          ...(meeting.transcript || []).map((line) => line.text),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  }, [visiblePast, historyTab, activeTopic, searchTerm, filters])

  // Star toggle — LOCAL-ONLY state (no backend persistence). Flipping `starred`
  // on the past list keeps the ★ fill, the "Starred" tab count, and the Starred
  // filter all in sync automatically since they all derive from `past`.
  const handleToggleStar = React.useCallback(
    (meetingId: string) => {
      const target = past.find((m) => m.id === meetingId)
      const nextStarred = !target?.starred
      setPast((prev) =>
        prev.map((m) => (m.id === meetingId ? { ...m, starred: nextStarred } : m)),
      )
      // Persist server-side (fire-and-forget; optimistic UI already applied).
      if (resolvedClientId) void MeetingsService.setStar(resolvedClientId, meetingId, nextStarred)
      trackMeetingsInteraction(nextStarred ? 'star_add' : 'star_remove', { meetingId })
    },
    [past, resolvedClientId, trackMeetingsInteraction],
  )

  const hasClient = Boolean(resolvedClientId)
  const advisorName = advisor?.name || 'your advisor'

  return (
    <ClientDashboardV3Layout activeTab="dashboard">
      <div className="space-y-5">
        {/* Page title block */}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#DC6803]">
            Management One
          </div>
          <h1 className="mt-1 text-2xl font-bold text-[#101828]">Your Advisor</h1>
          <p className="mt-1 text-sm text-[#667085]">
            Your relationship with {advisorName} — calls, notes, and history.
          </p>
        </div>

        <AdvisorHeader
          advisor={advisor}
          loading={loading}
          hasClient={hasClient}
          clientName={resolvedClientName}
          hubspotSchedulingUrl={hubspotSchedulingUrl}
          onEmail={() => trackMeetingsInteraction('Advisor Email Clicked', { advisorId: advisor?.id })}
          onSchedule={() => trackMeetingsInteraction('Advisor Schedule Clicked', { advisorId: advisor?.id })}
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          {/* Left column: next call + upcoming */}
          <div className="space-y-5">
            <NextCallCard
              meeting={nextCall}
              loading={loading}
              hubspotSchedulingUrl={hubspotSchedulingUrl}
              onJoin={() => trackMeetingsInteraction('Next Call Join Clicked', { meetingId: nextCall?.id })}
              onReschedule={() => trackMeetingsInteraction('Next Call Reschedule Clicked', { meetingId: nextCall?.id })}
              onToggleAgenda={(index, text) =>
                trackMeetingsInteraction('Agenda Item Toggled', { meetingId: nextCall?.id, index, text })}
            />
            <UpcomingCallsCard
              meetings={upcomingList}
              showList={showUpcomingList}
              calendarMeetings={calendarMeetings}
              calendarEvents={m1Events}
              hubspotSchedulingUrl={hubspotSchedulingUrl}
              loading={loading}
              onOpenCalendar={() => trackMeetingsInteraction('Open HubSpot Calendar Clicked')}
            />
          </div>

          {/* Right column: call history */}
          <CallHistory
            meetings={filteredPast}
            loading={loading}
            allCount={allCount}
            starredCount={starredCount}
            newCount={newCount}
            sinceLabel={sinceLabel}
            clientId={resolvedClientId}
            clientCode={resolvedClientCode}
            topics={topicOptions}
            filters={filters}
            onFiltersChange={setFilters}
            historyTab={historyTab}
            onHistoryTabChange={(tab) => {
              setHistoryTab(tab)
              trackMeetingsInteraction('History Tab Changed', { tab })
            }}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            activeTopic={activeTopic}
            onTopicChange={(topic) => {
              setActiveTopic(topic)
              trackMeetingsInteraction('Topic Chip Toggled', { topic })
            }}
            onToggleStar={handleToggleStar}
            onRowExpand={(meetingId) =>
              trackMeetingsInteraction('Meeting Row Expanded', { meetingId })}
            onRowCollapse={(meetingId) =>
              trackMeetingsInteraction('Meeting Row Collapsed', { meetingId })}
            onDetailTabChange={(meetingId, tab) =>
              trackMeetingsInteraction('Meeting Detail Tab Changed', { meetingId, tab })}
            onAskIndie={(meetingId) =>
              trackMeetingsInteraction('Ask Indie Clicked', { meetingId })}
            onAskIndieMenu={(meetingId) =>
              trackMeetingsInteraction('ask_indie_menu', { meetingId })}
            onHideMeeting={handleHideMeeting}
            onUndoHide={handleUndoHideMeeting}
            onFilter={() => trackMeetingsInteraction('History Filter Clicked')}
            onExport={() => trackMeetingsInteraction('History Export Clicked')}
            onLoadEarlier={() => trackMeetingsInteraction('Load Earlier Calls Clicked')}
          />
        </div>
      </div>
    </ClientDashboardV3Layout>
  )
}

// ---- Advisor header ------------------------------------------------------

function AdvisorHeader({
  advisor,
  loading,
  hasClient,
  clientName,
  hubspotSchedulingUrl,
  onEmail,
  onSchedule,
}: {
  advisor: MeetingAdvisor | null
  loading: boolean
  hasClient: boolean
  clientName: string
  hubspotSchedulingUrl: string | null
  onEmail: () => void
  onSchedule: () => void
}) {
  if (loading) {
    return (
      <Card className="border-[#EAECF0] shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="h-14 w-14 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-3 w-44" />
              </div>
            </div>
            <div className="flex gap-3">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!advisor) {
    return (
      <Card className="border-dashed border-[#D0D5DD] bg-[#F9FAFB]">
        <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF2FF]">
            <UserRound className="h-6 w-6 text-[#534AB7]" strokeWidth={1.6} />
          </div>
          <div className="text-base font-semibold text-[#101828]">
            {hasClient ? 'No advisor assigned yet' : 'Select a client to see your advisor'}
          </div>
          <p className="max-w-md text-sm text-[#667085]">
            {hasClient
              ? 'Your Management One advisor will appear here once they are assigned to this account.'
              : 'Choose a client from the switcher to view their advisor and call history.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  const stats = advisor.relationshipStats || []
  const [scheduleOpen, setScheduleOpen] = React.useState(false)

  return (
    <Card className="border-[#EAECF0] shadow-sm">
      <CardContent className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between">
        {/* Identity */}
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12">
            {advisor.avatarUrl ? <AvatarImage src={advisor.avatarUrl} alt={advisor.name} /> : null}
            <AvatarFallback className="bg-[#F4EBFF] text-sm font-bold text-[#6941C6]">
              {advisor.initials || initialsFor(advisor.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-base font-bold text-[#101828]">{advisor.name}</div>
            <div className="mt-0.5 text-[13px] text-[#667085]">{advisor.title}</div>
            {advisor.relationshipLabel ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#17B26A]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#17B26A]" />
                {advisor.relationshipLabel}
              </div>
            ) : null}
          </div>
        </div>

        {/* Stats */}
        {stats.length ? (
          <div className="flex items-center">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={cn('px-6', index > 0 && 'border-l border-[#EAECF0]')}
              >
                <div className="text-xl font-bold text-[#101828]">{stat.value}</div>
                <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-[#98A2B3]">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Actions — real: mailto + Google Calendar event-compose deep link (SPS-5029). */}
        <div className="flex flex-shrink-0 gap-2.5">
          <Button
            variant="outline"
            className="h-9 gap-2 border-[#EAECF0] text-[13px] text-[#344054]"
            disabled={!advisor.email}
            onClick={() => {
              if (advisor.email) window.location.href = `mailto:${advisor.email}`
              onEmail()
            }}
          >
            <Mail className="h-4 w-4" strokeWidth={1.7} />
            Send email
          </Button>
          <Button
            className="h-9 gap-2 bg-[#7F56D9] text-[13px] text-white hover:bg-[#6941C6]"
            onClick={() => {
              setScheduleOpen(true)
              onSchedule()
            }}
          >
            <CalendarDays className="h-4 w-4" strokeWidth={1.7} />
            Schedule call
          </Button>
        </div>
      </CardContent>

      {/* Opens the client's HubSpot meetings/calendar embed (replaces Google Calendar). */}
      <HubSpotCalendarModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        title={`Schedule a call${clientName ? ` with ${clientName}` : ''}`}
        embedUrl={buildHubspotEmbedUrl(hubspotSchedulingUrl)}
      />
    </Card>
  )
}

// ---- Next call card ------------------------------------------------------

function NextCallCard({
  meeting,
  loading,
  hubspotSchedulingUrl,
  onJoin,
  onReschedule,
  onToggleAgenda,
}: {
  meeting: Meeting | null
  loading: boolean
  hubspotSchedulingUrl: string | null
  onJoin: () => void
  onReschedule: () => void
  onToggleAgenda: (index: number, text: string) => void
}) {
  const [checkedAgenda, setCheckedAgenda] = React.useState<Record<number, boolean>>({})
  const [rescheduleOpen, setRescheduleOpen] = React.useState(false)

  React.useEffect(() => {
    const seeded: Record<number, boolean> = {}
    ;(meeting?.agendaItems || []).forEach((item, index) => {
      if (item.done) seeded[index] = true
    })
    setCheckedAgenda(seeded)
  }, [meeting?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Card className="border-[#EAECF0] shadow-sm">
        <CardContent className="space-y-4 p-5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <div className="flex gap-3">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-28" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!meeting) {
    return (
      <Card className="border-dashed border-[#D0D5DD] bg-[#F9FAFB]">
        <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
          <CalendarDays className="h-6 w-6 text-[#98A2B3]" strokeWidth={1.6} />
          <div className="text-sm font-semibold text-[#344054]">No upcoming call scheduled</div>
          <p className="max-w-xs text-xs text-[#667085]">
            When your advisor schedules the next call, it will show up here with the agenda.
          </p>
        </CardContent>
      </Card>
    )
  }

  const agenda = meeting.agendaItems || []

  return (
    <Card className="border-[#EAECF0] shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#98A2B3]">Next call</span>
          {meeting.relativeLabel ? <AmberPill>{meeting.relativeLabel}</AmberPill> : null}
        </div>

        <h3 className="mt-3 text-lg font-bold text-[#101828]">
          {meeting.dateLabel || formatNextCallDate(meeting.date)}
        </h3>
        <div className="mt-1 text-[13px] text-[#667085]">
          {meeting.timeMeta || [formatMeetingDate(meeting.date), meeting.duration].filter(Boolean).join(' · ')}
        </div>

        {/* Indie pre-call brief */}
        {meeting.briefTitle || meeting.brief ? (
          <div className="mt-4 flex gap-2.5 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] p-3">
            <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#DC6803]" strokeWidth={1.8} />
            <div>
              {meeting.briefTitle ? (
                <div className="text-[12px] font-semibold text-[#B54708]">{meeting.briefTitle}</div>
              ) : null}
              {meeting.brief ? (
                <div className="mt-0.5 text-[11px] leading-snug text-[#B54708]/90">{meeting.brief}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Agenda */}
        {agenda.length ? (
          <div className="mt-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#98A2B3]">Agenda</div>
            <ul className="space-y-1">
              {agenda.map((item, index) => {
                const checked = Boolean(checkedAgenda[index])
                return (
                  <li key={`${item.text}-${index}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setCheckedAgenda((prev) => ({ ...prev, [index]: !prev[index] }))
                        onToggleAgenda(index, item.text)
                      }}
                      className="flex w-full items-start gap-2 rounded-md px-1 py-1 text-left text-[13px] text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      {checked ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#17B26A]" strokeWidth={1.8} />
                      ) : (
                        <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#D0D5DD]" strokeWidth={1.8} />
                      )}
                      <span className={cn(checked && 'text-[#98A2B3] line-through')}>{item.text}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        <div className="mt-5 flex gap-2.5">
          <Button className="flex-1 gap-2 bg-[#7F56D9] text-[13px] text-white hover:bg-[#6941C6]" onClick={onJoin}>
            <Video className="h-4 w-4" strokeWidth={1.7} />
            Join call
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-[#EAECF0] text-[13px] text-[#344054]"
            onClick={() => {
              setRescheduleOpen(true)
              onReschedule()
            }}
          >
            <CalendarDays className="h-4 w-4" strokeWidth={1.7} />
            Reschedule
          </Button>
        </div>
      </CardContent>

      {/* Reschedule via HubSpot: there is no per-meeting reschedule URL, so this opens the
          same per-client advisor scheduling page used for "Schedule call". */}
      <HubSpotCalendarModal
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        title={`Reschedule${meeting.title ? ` — ${meeting.title}` : ' call'}`}
        embedUrl={buildHubspotEmbedUrl(hubspotSchedulingUrl)}
      />
    </Card>
  )
}

// ---- Upcoming calls card (+ mini calendar) -------------------------------

function UpcomingCallsCard({
  meetings,
  showList,
  calendarMeetings,
  calendarEvents,
  hubspotSchedulingUrl,
  loading,
  onOpenCalendar,
}: {
  meetings: Meeting[]
  showList: boolean
  calendarMeetings: Meeting[]
  calendarEvents: M1Event[]
  hubspotSchedulingUrl: string | null
  loading: boolean
  onOpenCalendar: () => void
}) {
  const [showCalendar, setShowCalendar] = React.useState(true)
  const [hubspotOpen, setHubspotOpen] = React.useState(false)

  return (
    <Card className="border-[#EAECF0] shadow-sm">
      <CardContent className="p-5">
        {/* Rule A: with a single upcoming meeting, suppress the whole "Upcoming calls"
            section (title + list). Only the "Hide calendar" toggle stays so the user can
            still collapse the mini-calendar, which remains below alongside the Next-call card. */}
        <div className="mb-3 flex items-center justify-between">
          {showList || loading ? (
            <h3 className="text-[13px] font-bold text-[#101828]">Upcoming calls</h3>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => setShowCalendar((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#53389E] hover:underline"
          >
            <CalendarDays className="h-3 w-3" strokeWidth={2} />
            {showCalendar ? 'Hide calendar' : 'Show calendar'}
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : !showList ? null : meetings.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#EAECF0] p-5 text-center text-[13px] text-[#667085]">
            No other upcoming calls.
          </div>
        ) : (
          <ul>
            {meetings.map((meeting, index) => (
              <li
                key={meeting.id}
                className={cn(
                  'flex items-center gap-3 py-2.5',
                  index < meetings.length - 1 && 'border-b border-[#F2F4F7]',
                )}
              >
                <div className="flex w-9 flex-shrink-0 flex-col items-center">
                  <span className="font-mono text-lg font-bold leading-none text-[#1D2939]">
                    {meeting.dayNum || new Date(meeting.date).getDate()}
                  </span>
                  <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#98A2B3]">
                    {meeting.monthShort ||
                      new Date(meeting.date).toLocaleDateString(undefined, { month: 'short' }).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold text-[#101828]">{meeting.title}</div>
                  {meeting.timeMeta ? (
                    <div className="font-mono text-[11px] text-[#98A2B3]">{meeting.timeMeta}</div>
                  ) : null}
                </div>
                {meeting.relativeLabel ? <AmberPill>{meeting.relativeLabel}</AmberPill> : null}
              </li>
            ))}
          </ul>
        )}

        {showCalendar ? (
          <div className="mt-4 border-t border-[#EAECF0] pt-4">
            <MiniCalendar meetings={calendarMeetings} events={calendarEvents} />
            <button
              type="button"
              onClick={() => {
                setHubspotOpen(true)
                onOpenCalendar()
              }}
              className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#6941C6] hover:underline"
            >
              <ExternalLink className="h-3 w-3" strokeWidth={2} />
              Open HubSpot calendar
            </button>
          </div>
        ) : null}
      </CardContent>

      {/* Client's HubSpot meetings/calendar embed (replaces the old Google Calendar link). */}
      <HubSpotCalendarModal
        open={hubspotOpen}
        onOpenChange={setHubspotOpen}
        title="HubSpot calendar"
        embedUrl={buildHubspotEmbedUrl(hubspotSchedulingUrl)}
      />
    </Card>
  )
}

// A dynamic mini month calendar (SPS-5005). Opens on the ACTUAL current month and dots
// the days that have activity for this client, by type (markers are ADDITIVE — a day can
// show several together):
//   • past calls      → orange (#f97215)   [from meetings, scope 'past']
//   • upcoming calls   → purple (#7f56d9)   [from meetings, scope 'upcoming'/'next']
//   • M1 Events        → green  (#17B26A)   [from the Client Notifications 'event' type,
//                                            client-scoped: all-clients + this client]
// All derived from live data, never hardcoded.
function MiniCalendar({ meetings, events }: { meetings: Meeting[]; events: M1Event[] }) {
  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
  const today = React.useMemo(() => new Date(), [])
  const [viewDate, setViewDate] = React.useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  )
  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()

  // Cap backward navigation at ~2 months before the current month (e.g. in Nov → Sep).
  // Forward navigation is unaffected. The prev (‹) arrow is disabled at this floor.
  const minMonth = React.useMemo(
    () => new Date(today.getFullYear(), today.getMonth() - 2, 1),
    [today],
  )
  const atMinMonth =
    year < minMonth.getFullYear() ||
    (year === minMonth.getFullYear() && month <= minMonth.getMonth())

  // day-of-month -> which marker types land on it this view month.
  const marks = React.useMemo(() => {
    const map = new Map<number, { past: boolean; upcoming: boolean; event: boolean; eventActive: boolean }>()
    const ensure = (day: number) => {
      const entry = map.get(day) || { past: false, upcoming: false, event: false, eventActive: false }
      map.set(day, entry)
      return entry
    }
    // Advisor calls (orange past / purple upcoming) — existing behavior, kept intact.
    for (const meeting of meetings) {
      const date = new Date(meeting.date)
      if (Number.isNaN(date.getTime())) continue
      if (date.getFullYear() !== year || date.getMonth() !== month) continue
      const entry = ensure(date.getDate())
      if (meeting.scope === 'past') entry.past = true
      else entry.upcoming = true
    }
    // M1 Events (green) — additive. Includes past/out-of-window + non-active events; a day
    // with only non-active (draft/inactive) events renders a dimmed green dot.
    for (const ev of events) {
      const raw = ev.event_date
      if (!raw) continue
      // date-only strings parse as local midnight to avoid a UTC off-by-one.
      const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw)
      if (Number.isNaN(date.getTime())) continue
      if (date.getFullYear() !== year || date.getMonth() !== month) continue
      const entry = ensure(date.getDate())
      entry.event = true
      if ((ev.status ?? 'active') === 'active') entry.eventActive = true
    }
    return map
  }, [meetings, events, year, month])

  const firstWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7
  const cells = Array.from({ length: totalCells }, (_, index) => {
    const dayNum = index - firstWeekday + 1
    return { index, day: dayNum >= 1 && dayNum <= daysInMonth ? dayNum : null }
  })

  const isToday = (day: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === day

  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[12px] font-bold text-[#101828]">{monthLabel}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setViewDate(new Date(year, month - 1, 1))}
            disabled={atMinMonth}
            className="flex h-6 w-6 items-center justify-center rounded-lg border border-[#EAECF0] text-[#667085] hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => setViewDate(new Date(year, month + 1, 1))}
            className="flex h-6 w-6 items-center justify-center rounded-lg border border-[#EAECF0] text-[#667085] hover:bg-[#F9FAFB]"
            aria-label="Next month"
          >
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center">
        {weekdays.map((wd) => (
          <div key={wd} className="text-[9px] font-semibold uppercase text-[#98A2B3]">
            {wd}
          </div>
        ))}
        {cells.map((cell) => {
          const mark = cell.day ? marks.get(cell.day) : undefined
          const todayCell = cell.day ? isToday(cell.day) : false
          return (
            <div key={cell.index} className="flex justify-center">
              <div className="relative flex h-6 w-6 items-center justify-center">
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-[11px]',
                    !cell.day && 'text-transparent',
                    cell.day && !todayCell && 'text-[#475467]',
                    todayCell && 'bg-[#F4EBFF] font-bold text-[#53389E]',
                  )}
                >
                  {cell.day ?? ''}
                </span>
                {mark && (mark.past || mark.upcoming || mark.event) ? (
                  <span className="absolute -bottom-0.5 left-1/2 flex -translate-x-1/2 items-center gap-0.5">
                    {mark.past ? <span className="h-1.5 w-1.5 rounded-full bg-[#f97215] ring-1 ring-white" /> : null}
                    {mark.upcoming ? <span className="h-1.5 w-1.5 rounded-full bg-[#7f56d9] ring-1 ring-white" /> : null}
                    {mark.event ? (
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full bg-[#17B26A] ring-1 ring-white',
                          !mark.eventActive && 'opacity-50',
                        )}
                      />
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-[10px] text-[#667085]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#f97215]" />
          Past call
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-[#667085]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#7f56d9]" />
          Upcoming
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-[#667085]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#17B26A]" />
          M1 event
        </span>
      </div>
    </div>
  )
}

// ---- Call history --------------------------------------------------------

function CallHistory({
  meetings,
  loading,
  allCount,
  starredCount,
  newCount,
  sinceLabel,
  clientId,
  clientCode,
  topics,
  filters,
  onFiltersChange,
  historyTab,
  onHistoryTabChange,
  searchTerm,
  onSearchChange,
  activeTopic,
  onTopicChange,
  onToggleStar,
  onRowExpand,
  onRowCollapse,
  onDetailTabChange,
  onAskIndie,
  onAskIndieMenu,
  onHideMeeting,
  onUndoHide,
  onFilter,
  onExport,
  onLoadEarlier,
}: {
  meetings: Meeting[]
  loading: boolean
  allCount: number
  starredCount: number
  newCount: number
  sinceLabel: string | null
  clientId: string | number
  clientCode: string
  topics: string[]
  filters: HistoryFilters
  onFiltersChange: (filters: HistoryFilters) => void
  historyTab: HistoryTab
  onHistoryTabChange: (tab: HistoryTab) => void
  searchTerm: string
  onSearchChange: (value: string) => void
  activeTopic: string | null
  onTopicChange: (topic: string | null) => void
  onToggleStar: (meetingId: string) => void
  onRowExpand: (meetingId: string) => void
  onRowCollapse: (meetingId: string) => void
  onDetailTabChange: (meetingId: string, tab: string) => void
  onAskIndie: (meetingId: string) => void
  onAskIndieMenu: (meetingId: string) => void
  onHideMeeting: (meetingId: string) => void
  onUndoHide: (meetingId: string) => void
  onFilter: () => void
  onExport: () => void
  onLoadEarlier: () => void
}) {
  // Single-open accordion: all rows start collapsed; the expanded detail renders
  // in place under whichever row the user clicks. No row is force-expanded.
  const [expandedId, setExpandedId] = React.useState<string | null>(null)

  // Client-side pagination over the filtered list. Start with the 10 most-recent
  // calls; "Load earlier" reveals the next batch. Reset whenever the filters
  // (tab / search / topic) change so the count always reflects the current set.
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE)
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [historyTab, searchTerm, activeTopic, filters])

  const filterCount = activeFilterCount(filters, activeTopic)

  const visibleMeetings = meetings.slice(0, visibleCount)
  const remaining = Math.max(0, meetings.length - visibleCount)
  const nextBatch = Math.min(remaining, PAGE_SIZE)

  const toggleRow = (id: string) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id
      if (next) {
        onRowExpand(id)
      } else {
        onRowCollapse(id)
      }
      return next
    })
  }

  const tabs: Array<{ key: HistoryTab; label: string; count: number; icon?: React.ReactNode }> = [
    { key: 'all', label: 'All', count: allCount },
    { key: 'starred', label: 'Starred', count: starredCount, icon: <Star className="h-3.5 w-3.5" strokeWidth={1.8} /> },
    { key: 'new', label: 'New', count: newCount },
  ]

  return (
    <Card className="border-[#EAECF0] shadow-sm">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex flex-col gap-3 px-5 pt-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-base font-bold text-[#101828]">Call history</h3>
            {loading ? (
              <Skeleton className="mt-1 h-3.5 w-40" />
            ) : (
              <p className="mt-0.5 text-[12px] text-[#667085]">
                {allCount} {allCount === 1 ? 'interaction' : 'interactions'}
                {sinceLabel ? ` · since ${sinceLabel}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98A2B3]" strokeWidth={1.7} />
              <Input
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search calls, notes, transcripts…"
                className="h-9 w-56 border-[#EAECF0] pl-9 text-[13px]"
              />
            </div>
            <CallHistoryFilterMenu
              topics={topics}
              filters={filters}
              onFiltersChange={onFiltersChange}
              activeTopic={activeTopic}
              onTopicChange={onTopicChange}
              activeCount={filterCount}
              onOpen={onFilter}
            />
            <Button
              variant="outline"
              className="h-9 gap-1.5 border-[#EAECF0] text-[13px] text-[#344054]"
              disabled={meetings.length === 0}
              onClick={() => {
                downloadCallHistoryCsv(meetings, clientCode)
                onExport()
              }}
            >
              <Download className="h-4 w-4" strokeWidth={1.7} />
              Export
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex items-center gap-6 border-b border-[#EAECF0] px-5">
          {tabs.map((tab) => {
            const active = historyTab === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onHistoryTabChange(tab.key)}
                className={cn(
                  '-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 pt-1 text-[13px] font-medium transition-colors',
                  active
                    ? 'border-[#7F56D9] font-semibold text-[#101828]'
                    : 'border-transparent text-[#667085] hover:text-[#344054]',
                )}
              >
                {tab.icon}
                {tab.label}
                {!loading ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                      active ? 'bg-[#F4EBFF] text-[#6941C6]' : 'bg-[#F2F4F7] text-[#667085]',
                    )}
                  >
                    {tab.count}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>

        {/* Topic chips — SPS-5005: derived from the client's actual meeting topics, sorted. */}
        {topics.length ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-[#EAECF0] px-5 py-3">
            <span className="text-[11px] font-medium text-[#98A2B3]">Topic:</span>
            <TopicChip label="All" active={activeTopic === null} onClick={() => onTopicChange(null)} />
            {topics.map((topic) => (
              <TopicChip
                key={topic}
                label={topic}
                active={activeTopic === topic}
                onClick={() => onTopicChange(activeTopic === topic ? null : topic)}
              />
            ))}
          </div>
        ) : null}

        {/* List */}
        {loading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : meetings.length === 0 ? (
          <div className="p-10 text-center">
            <CalendarDays className="mx-auto h-6 w-6 text-[#98A2B3]" strokeWidth={1.6} />
            <div className="mt-2 text-sm font-semibold text-[#344054]">
              {allCount === 0 ? 'No past calls yet' : 'No calls match your filters'}
            </div>
            <p className="mt-1 text-xs text-[#667085]">
              {allCount === 0
                ? 'Past calls with your advisor will be listed here with summaries and transcripts.'
                : 'Try clearing the search, topic, or tab filters.'}
            </p>
          </div>
        ) : (
          <div>
            {visibleMeetings.map((meeting) => (
              <CallRow
                key={meeting.id}
                meeting={meeting}
                clientId={clientId}
                clientCode={clientCode}
                expanded={expandedId === meeting.id}
                onToggle={() => toggleRow(meeting.id)}
                onToggleStar={onToggleStar}
                onDetailTabChange={onDetailTabChange}
                onAskIndie={onAskIndie}
                onAskIndieMenu={onAskIndieMenu}
                onHideMeeting={onHideMeeting}
                onUndoHide={onUndoHide}
              />
            ))}
          </div>
        )}

        {/* Footer — real pagination. Show the button only while hidden calls
            remain; the label reflects the actual next-batch size. */}
        {!loading && meetings.length > 0 ? (
          <div className="flex justify-center border-t border-[#EAECF0] py-4">
            {remaining > 0 ? (
              <Button
                variant="outline"
                className="h-9 gap-1.5 border-[#EAECF0] text-[13px] text-[#475467]"
                onClick={() => {
                  setVisibleCount((count) => count + PAGE_SIZE)
                  onLoadEarlier()
                }}
              >
                <ChevronDown className="h-4 w-4" strokeWidth={1.8} />
                Load {nextBatch} earlier {nextBatch === 1 ? 'call' : 'calls'}
              </Button>
            ) : (
              <span className="text-[12px] text-[#98A2B3]">All calls loaded</span>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function TopicChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
        active
          ? 'bg-[#101828] text-white'
          : 'bg-[#F2F4F7] text-[#344054] hover:bg-[#EAECF0]',
      )}
    >
      {label}
    </button>
  )
}

// Call-history "Filter" popover. Toggles for recording / open-actions, a topic select
// (reusing the real topic list + the shared activeTopic), and a simple date range. All
// of it composes with the search box, the tabs, and the topic chips. An active-filter
// count badge sits on the trigger.
function CallHistoryFilterMenu({
  topics,
  filters,
  onFiltersChange,
  activeTopic,
  onTopicChange,
  activeCount,
  onOpen,
}: {
  topics: string[]
  filters: HistoryFilters
  onFiltersChange: (filters: HistoryFilters) => void
  activeTopic: string | null
  onTopicChange: (topic: string | null) => void
  activeCount: number
  onOpen: () => void
}) {
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) onOpen()
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-1.5 border-[#EAECF0] text-[13px] text-[#344054]">
          <ListFilter className="h-4 w-4" strokeWidth={1.7} />
          Filter
          {activeCount > 0 ? (
            <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7F56D9] px-1 text-[10px] font-bold text-white">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-bold text-[#101828]">Filter calls</span>
            {activeCount > 0 ? (
              <button
                type="button"
                className="text-[11px] font-semibold text-[#6941C6] hover:underline"
                onClick={() => {
                  onFiltersChange(EMPTY_FILTERS)
                  onTopicChange(null)
                }}
              >
                Clear all
              </button>
            ) : null}
          </div>

          <label className="flex cursor-pointer items-center gap-2.5">
            <Checkbox
              checked={filters.hasRecording}
              onCheckedChange={(checked) =>
                onFiltersChange({ ...filters, hasRecording: checked === true })}
            />
            <span className="text-[13px] text-[#344054]">Has recording</span>
          </label>

          <label className="flex cursor-pointer items-center gap-2.5">
            <Checkbox
              checked={filters.hasOpenActions}
              onCheckedChange={(checked) =>
                onFiltersChange({ ...filters, hasOpenActions: checked === true })}
            />
            <span className="text-[13px] text-[#344054]">Has open actions</span>
          </label>

          {topics.length ? (
            <div>
              <div className="mb-1.5 text-[11px] font-semibold text-[#667085]">Topic</div>
              <Select
                value={activeTopic ?? 'all'}
                onValueChange={(value) => onTopicChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="h-8 text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All topics</SelectItem>
                  {topics.map((topic) => (
                    <SelectItem key={topic} value={topic}>
                      {topic}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div>
            <div className="mb-1.5 text-[11px] font-semibold text-[#667085]">Date range</div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={(event) => onFiltersChange({ ...filters, dateFrom: event.target.value })}
                className="h-8 text-[12px]"
              />
              <span className="text-[12px] text-[#98A2B3]">to</span>
              <Input
                type="date"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={(event) => onFiltersChange({ ...filters, dateTo: event.target.value })}
                className="h-8 text-[12px]"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ---- Call row (collapsed header + expanded detail) -----------------------

function CallRow({
  meeting,
  clientId,
  clientCode,
  expanded,
  onToggle,
  onToggleStar,
  onDetailTabChange,
  onAskIndie,
  onAskIndieMenu,
  onHideMeeting,
  onUndoHide,
}: {
  meeting: Meeting
  clientId: string | number
  clientCode: string
  expanded: boolean
  onToggle: () => void
  onToggleStar: (meetingId: string) => void
  onDetailTabChange: (meetingId: string, tab: string) => void
  onAskIndie: (meetingId: string) => void
  onAskIndieMenu: (meetingId: string) => void
  onHideMeeting: (meetingId: string) => void
  onUndoHide: (meetingId: string) => void
}) {
  // Lazily hydrate the two fields the list endpoint omits by design: the transcript
  // (a separate call) and the signed video URL (resolved on demand, ~1h TTL). The
  // fixtures carry both inline, so this is a no-op for the local preview; against the
  // real backend it fills them in the first time the row is expanded.
  const [lazyTranscript, setLazyTranscript] = React.useState<MeetingTranscriptLine[] | null>(null)
  const [lazyVideoUrl, setLazyVideoUrl] = React.useState<string | null>(null)

  // When the row is opened by clicking the thumbnail or the title, the recording should
  // autoplay. Opening via the plain "Expand" button does NOT autoplay (less jarring).
  const [autoPlayOnExpand, setAutoPlayOnExpand] = React.useState(false)
  React.useEffect(() => {
    // Reset once collapsed so a later Expand-button open won't inherit autoplay.
    if (!expanded) setAutoPlayOnExpand(false)
  }, [expanded])

  // Thumbnail / title click → toggle this row, requesting autoplay when it's opening.
  const handleMediaToggle = () => {
    if (!expanded) setAutoPlayOnExpand(true)
    onToggle()
  }

  React.useEffect(() => {
    if (!expanded || !clientId) return
    let cancelled = false

    // Clear the "New" badge for this call (fire-and-forget).
    void MeetingsService.markViewed(clientId, meeting.id)

    const alreadyHasTranscript = Array.isArray(meeting.transcript) && meeting.transcript.length > 0
    if (lazyTranscript === null && !alreadyHasTranscript) {
      MeetingsService.getTranscript(clientId, meeting.id, clientCode)
        .then((lines) => { if (!cancelled) setLazyTranscript(lines || []) })
        .catch(() => { if (!cancelled) setLazyTranscript([]) })
    }
    if (meeting.hasRecording && !meeting.videoUrl && lazyVideoUrl === null) {
      MeetingsService.getRecordingUrl(clientId, meeting.id, clientCode)
        .then((url) => { if (!cancelled) setLazyVideoUrl(url) })
        .catch(() => { if (!cancelled) setLazyVideoUrl(null) })
    }

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, clientId, clientCode, meeting.id])

  // `meeting` + the lazily-resolved transcript/video. The list already supplies
  // everything else (summary, actions, attendees, topics), so only these two merge in.
  const enrichedMeeting: Meeting = {
    ...meeting,
    transcript:
      Array.isArray(meeting.transcript) && meeting.transcript.length > 0
        ? meeting.transcript
        : lazyTranscript ?? meeting.transcript,
    videoUrl: meeting.videoUrl ?? lazyVideoUrl,
  }

  const metaChips: React.ReactNode[] = []
  if (meeting.typeLabel) {
    metaChips.push(
      <span key="type" className="rounded-full border border-[#E9D7FE] bg-[#F4EBFF] px-2 py-0.5 text-[11px] font-semibold text-[#53389E]">
        {meeting.typeLabel}
      </span>,
    )
  }
  metaChips.push(
    meeting.hasRecording ? (
      <span key="rec" className="rounded-full border border-[#E9D7FE] bg-[#F4EBFF] px-2 py-0.5 text-[11px] font-semibold text-[#6941C6]">
        Recording
      </span>
    ) : (
      <span key="norec" className="rounded-full bg-[#F2F4F7] px-2 py-0.5 text-[11px] font-semibold text-[#667085]">
        No recording
      </span>
    ),
  )
  ;(meeting.topics || []).forEach((topic) =>
    metaChips.push(
      <span key={topic} className="rounded-full bg-[#F2F4F7] px-2 py-0.5 text-[11px] font-semibold text-[#344054]">
        {topic}
      </span>,
    ),
  )

  return (
    <div className="border-b border-[#F2F4F7] last:border-b-0">
      {/* Collapsed header */}
      <div className="flex items-start gap-3 px-5 py-4">
        {/* Thumbnail — click to expand (and autoplay the recording) */}
        <button
          type="button"
          onClick={handleMediaToggle}
          className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7F56D9]/40"
          aria-label={expanded ? 'Collapse call' : 'Expand call and play recording'}
          aria-expanded={expanded}
        >
          <CallThumbnail meeting={meeting} />
        </button>

        {/* Middle */}
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={handleMediaToggle}
            className="block w-full text-left text-[14px] font-bold text-[#101828] hover:text-[#53389E]"
            aria-expanded={expanded}
          >
            {meeting.title}
          </button>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {metaChips}
            <span className="font-mono text-[12px] text-[#98A2B3]">· {meeting.dateLabel || formatMeetingDate(meeting.date)}</span>
          </div>
          <div className="mt-2">
            <AttendeeList attendees={meeting.attendees} duration={meeting.duration} />
          </div>
        </div>

        {/* Right controls */}
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => onToggleStar(meeting.id)}
            className={cn('p-1 hover:text-[#F79009]', meeting.starred ? 'text-[#F79009]' : 'text-[#D0D5DD]')}
            aria-label={meeting.starred ? 'Unstar call' : 'Star call'}
            aria-pressed={Boolean(meeting.starred)}
          >
            <Star
              className={cn('h-4 w-4', meeting.starred && 'fill-[#F79009] text-[#F79009]')}
              strokeWidth={1.8}
            />
          </button>
          <CallRowMoreMenu
            meeting={enrichedMeeting}
            onAskIndieMenu={onAskIndieMenu}
            onHideMeeting={onHideMeeting}
            onUndoHide={onUndoHide}
          />
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-[#475467] hover:bg-[#F9FAFB]"
          >
            <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} strokeWidth={2.2} />
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded ? (
        <MeetingDetail meeting={enrichedMeeting} clientId={clientId} autoPlay={autoPlayOnExpand} onDetailTabChange={onDetailTabChange} onAskIndie={onAskIndie} />
      ) : null}
    </div>
  )
}

// Triggers a browser download of the recording. meeting.videoUrl is a short-lived
// signed Grain URL resolved on demand by the media-proxy (client-scoped, server-held
// token) — never a stored/committed value.
function downloadRecording(meeting: Meeting) {
  if (!meeting.videoUrl) return
  const anchor = document.createElement('a')
  anchor.href = meeting.videoUrl
  anchor.download = `${(meeting.title || 'recording').replace(/[^a-z0-9]+/gi, '-')}.mp4`
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

// ---- transcript / Ask-Indie helpers (client-side only) --------------------

// A meeting has usable transcript content when it carries either a flat
// `plain_text` blob (some payloads) or at least one transcript segment.
// Most ORBIT1199 meetings have no transcript loaded, so this gates Copy /
// Download transcript.
function meetingHasTranscript(meeting: Meeting): boolean {
  const flat = (meeting as { plain_text?: unknown }).plain_text
  if (typeof flat === 'string' && flat.trim().length > 0) return true
  return Array.isArray(meeting.transcript) && meeting.transcript.length > 0
}

// Build a plain-text transcript: prefer a flat `plain_text` field, else join the
// segments as "Speaker: text" lines.
function buildTranscriptText(meeting: Meeting): string {
  const flat = (meeting as { plain_text?: unknown }).plain_text
  if (typeof flat === 'string' && flat.trim().length > 0) return flat.trim()
  return (meeting.transcript || [])
    .map((line) => (line.speaker ? `${line.speaker}: ${line.text}` : line.text))
    .join('\n')
}

function filenameSafe(value: string): string {
  return (value || 'transcript').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '')
}

// Triggers a browser download of a .txt transcript built from the loaded transcript
// (fetched from the client-scoped transcript endpoint on expand).
function downloadTranscript(meeting: Meeting) {
  const text = buildTranscriptText(meeting)
  if (!text) return
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${filenameSafe(meeting.title)}-transcript.txt`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// Seed the Indie panel with THIS call as context, mirroring the detail-panel
// "Ask Indie about this call" button (both go through requestOrbitIndiePanelOpen).
function buildMeetingAskIndiePrompt(meeting: Meeting): string {
  const when = meeting.dateLabel || formatMeetingDate(meeting.date)
  const parts: string[] = [`About my advisor call "${meeting.title}" (${when}):`]
  if (meeting.summary) {
    parts.push(`Summary — ${meeting.summary}`)
  } else if (meetingHasTranscript(meeting)) {
    parts.push(`Transcript —\n${buildTranscriptText(meeting).slice(0, 4000)}`)
  }
  parts.push('Help me understand the decisions made, the next steps, and anything else discussed on this call.')
  return parts.join('\n\n')
}

function openAskIndieForMeeting(meeting: Meeting, source: string) {
  requestOrbitIndiePanelOpen({
    source,
    prompt: buildMeetingAskIndiePrompt(meeting),
    submit: true,
  })
}

// Row "⋯" menu. Every item is wired client-side for the local test (SPS-5030):
//   Copy link / Share / Ask Indie — always available
//   Copy transcript / Download transcript — enabled only when the call has a
//     transcript loaded, otherwise disabled with a "No transcript available" hint
//   Open in Grain / Download recording — only render when their source exists
//   Delete — soft "remove from my view" (local state), never a backend/Grain delete
function CallRowMoreMenu({
  meeting,
  onAskIndieMenu,
  onHideMeeting,
  onUndoHide,
}: {
  meeting: Meeting
  onAskIndieMenu: (meetingId: string) => void
  onHideMeeting: (meetingId: string) => void
  onUndoHide: (meetingId: string) => void
}) {
  const hasGrain = Boolean(meeting.grainUrl)
  const hasVideo = Boolean(meeting.videoUrl)
  const hasTranscript = meetingHasTranscript(meeting)

  // Lightweight transient confirmation. The dropdown closes on item click, so the
  // toast renders as a fixed element that outlives the menu. `action` powers Undo.
  const [flash, setFlash] = React.useState<{ message: string; action?: { label: string; run: () => void } } | null>(null)
  const flashTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const showFlash = React.useCallback(
    (message: string, action?: { label: string; run: () => void }) => {
      if (flashTimer.current) clearTimeout(flashTimer.current)
      setFlash({ message, action })
      flashTimer.current = setTimeout(() => setFlash(null), action ? 5000 : 2200)
    },
    [],
  )
  React.useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current)
    },
    [],
  )

  const copyToClipboard = async (text: string, confirmation: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showFlash(confirmation)
    } catch {
      showFlash('Copy failed — check browser permissions')
    }
  }

  const handleShare = async () => {
    const url = window.location.href
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> }
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: meeting.title, url })
      } catch {
        /* user dismissed the share sheet — no-op */
      }
      return
    }
    await copyToClipboard(url, 'Link copied')
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#EAECF0] text-[#667085] outline-none hover:bg-[#F9FAFB] focus-visible:ring-2 focus-visible:ring-[#7F56D9]/40"
            aria-label="More options"
          >
            <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => copyToClipboard(window.location.href, 'Link copied')}>
            <LinkIcon className="h-4 w-4" strokeWidth={1.8} />
            Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShare}>
            <Share2 className="h-4 w-4" strokeWidth={1.8} />
            Share
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              openAskIndieForMeeting(meeting, 'meetings_menu')
              onAskIndieMenu(meeting.id)
            }}
          >
            <Sparkles className="h-4 w-4" strokeWidth={1.8} />
            Ask Indie
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            disabled={!hasTranscript}
            title={hasTranscript ? undefined : 'No transcript available'}
            onClick={hasTranscript ? () => copyToClipboard(buildTranscriptText(meeting), 'Transcript copied') : undefined}
          >
            <Copy className="h-4 w-4" strokeWidth={1.8} />
            Copy transcript
          </DropdownMenuItem>
          {hasVideo ? (
            <DropdownMenuItem onClick={() => downloadRecording(meeting)}>
              <Download className="h-4 w-4" strokeWidth={1.8} />
              Download recording
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            disabled={!hasTranscript}
            title={hasTranscript ? undefined : 'No transcript available'}
            onClick={hasTranscript ? () => downloadTranscript(meeting) : undefined}
          >
            <FileDown className="h-4 w-4" strokeWidth={1.8} />
            Download transcript
          </DropdownMenuItem>
          {hasGrain ? (
            <DropdownMenuItem
              onClick={() => window.open(meeting.grainUrl as string, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
              Open in Grain
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuSeparator />

          {/* SPS-5030: Delete is soft "remove from my view" only — real version persists
              via a backend hide endpoint; never hard-delete / never touch Grain. */}
          <DropdownMenuItem
            className="text-[#D92D20]"
            onClick={() => {
              onHideMeeting(meeting.id)
              showFlash('Removed from your view', {
                label: 'Undo',
                run: () => {
                  onUndoHide(meeting.id)
                  setFlash(null)
                },
              })
            }}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.8} />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {flash ? (
        <div className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 rounded-lg bg-[#101828] px-3.5 py-2.5 text-[12px] font-semibold text-white shadow-lg">
          <span>{flash.message}</span>
          {flash.action ? (
            <button
              type="button"
              onClick={flash.action.run}
              className="rounded-md px-1.5 py-0.5 text-[#D6BBFB] hover:text-white hover:underline"
            >
              {flash.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

function CallThumbnail({ meeting }: { meeting: Meeting }) {
  if (!meeting.hasRecording) {
    return (
      <div className="flex h-[62px] w-[100px] flex-shrink-0 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#98A2B3]">
        <CameraOff className="h-5 w-5" strokeWidth={1.7} />
      </div>
    )
  }
  return (
    <div
      className="relative flex h-[62px] w-[100px] flex-shrink-0 items-center justify-center overflow-hidden rounded-lg"
      style={{ backgroundImage: 'linear-gradient(148deg, #1D2939 0%, #344054 100%)' }}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90">
        <Play className="h-2.5 w-2.5 fill-[#1D2939] text-[#1D2939]" />
      </span>
      {meeting.recordingLength ? (
        <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1 font-mono text-[10px] font-semibold text-white">
          {meeting.recordingLength}
        </span>
      ) : null}
    </div>
  )
}

// ---- Expanded meeting detail ---------------------------------------------

interface ActionHandlers {
  keyFor: (item: MeetingActionItem, index: number) => string
  onAcceptAction: (key: string) => void
  onDismissAction: (key: string) => void
  onToggleActionDone: (key: string, next: boolean) => void
}

// Case-insensitive keyword/phrase highlighter for the meeting-detail search. Splits the
// text on the query and wraps every match in a <mark>; an empty query returns plain text.
function highlightMatches(text: string | null | undefined, query: string): React.ReactNode {
  const src = text ?? ''
  const q = query.trim()
  if (!q) return src
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = src.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((part, index) =>
    part && part.toLowerCase() === q.toLowerCase() ? (
      <mark key={index} className="rounded-sm bg-[#FEF08A] px-0.5 text-inherit">
        {part}
      </mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  )
}

// Renders summary-bullet text: converts **bold** spans to <strong> (stripping the
// asterisks) and applies the keyword highlight within every span.
function renderBulletText(text: string, query: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    const bold = /^\*\*([^*]+)\*\*$/.exec(part)
    if (bold) {
      return (
        <strong key={index} className="font-semibold text-[#1D2939]">
          {highlightMatches(bold[1], query)}
        </strong>
      )
    }
    return <React.Fragment key={index}>{highlightMatches(part, query)}</React.Fragment>
  })
}

function MeetingDetail({
  meeting,
  clientId,
  autoPlay = false,
  onDetailTabChange,
  onAskIndie,
}: {
  meeting: Meeting
  clientId: string | number
  autoPlay?: boolean
  onDetailTabChange: (meetingId: string, tab: string) => void
  onAskIndie: (meetingId: string) => void
}) {
  // Optimistic overrides for this session's action clicks (accept / dismiss / done),
  // keyed by each action's stable key. Persisted via MeetingsService; the UI updates
  // immediately and the server state is authoritative on the next load.
  const [actionOverrides, setActionOverrides] = React.useState<
    Record<string, { accepted?: boolean; dismissed?: boolean; done?: boolean }>
  >({})

  // Keyword/phrase search scoped to THIS meeting's detail — highlights matches across the
  // AI Summary (and key moments) and the Transcript. Empty box = no highlights.
  const [detailQuery, setDetailQuery] = React.useState('')

  const keyFor = (item: MeetingActionItem, index: number) =>
    (item as { key?: string }).key || String(index)

  const effectiveActions = (meeting.actionItems || [])
    .map((item, index) => {
      const ov = actionOverrides[keyFor(item, index)]
      if (!ov) return item
      if (ov.dismissed) return null
      if (ov.accepted || ov.done) {
        return {
          ...item,
          suggested: false,
          status: ov.done ? 'done' : 'open',
          badge: ov.done ? 'done_in_dashboard' : 'in_dashboard',
        } as MeetingActionItem
      }
      return item
    })
    .filter(Boolean) as MeetingActionItem[]

  const openCount = openActionCount(effectiveActions)

  const actionHandlers: ActionHandlers = {
    keyFor,
    onAcceptAction: (key) => {
      setActionOverrides((o) => ({ ...o, [key]: { ...o[key], accepted: true, dismissed: false } }))
      if (clientId) void MeetingsService.setActionState(clientId, meeting.id, key, 'accept')
    },
    onDismissAction: (key) => {
      setActionOverrides((o) => ({ ...o, [key]: { ...o[key], dismissed: true } }))
      if (clientId) void MeetingsService.setActionState(clientId, meeting.id, key, 'dismiss')
    },
    onToggleActionDone: (key, next) => {
      setActionOverrides((o) => ({ ...o, [key]: { ...o[key], accepted: true, done: next } }))
      if (clientId) void MeetingsService.setActionDone(clientId, meeting.id, key, next)
    },
  }
  const tabTriggerClass =
    'rounded-none border-b-2 border-transparent bg-transparent px-3.5 py-2.5 text-[13px] font-medium text-[#667085] shadow-none data-[state=active]:border-[#7F56D9] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-[#101828] data-[state=active]:shadow-none'

  return (
    <div className="border-t border-[#EAECF0] bg-white">
      {/* Video / recording area */}
      <div className="px-6 pt-4">
        {meeting.hasRecording && meeting.videoUrl ? (
          // In-page playback: src is a short-lived signed Grain URL from the media-proxy (resolved on expand).
          <div
            className="overflow-hidden rounded-lg"
            style={{ backgroundImage: 'linear-gradient(158deg, #0F1C2E 0%, #1D2939 100%)' }}
          >
            <video
              controls
              autoPlay={autoPlay}
              playsInline
              preload="metadata"
              src={meeting.videoUrl}
              poster={meeting.thumbnailUrl || undefined}
              className="h-[360px] w-full bg-black object-contain"
            />
          </div>
        ) : meeting.hasRecording ? (
          <div
            className="overflow-hidden rounded-lg"
            style={{ backgroundImage: 'linear-gradient(158deg, #0F1C2E 0%, #1D2939 100%)' }}
          >
            <div className="flex h-[360px] items-center justify-center">
              <button
                type="button"
                className="flex h-[52px] w-[52px] items-center justify-center rounded-full border-2 border-white/25 bg-white/10 p-3 text-white hover:bg-white/20"
                aria-label="Play recording"
              >
                <Play className="h-5 w-5 fill-white" />
              </button>
            </div>
            {/* Control bar */}
            <div className="flex items-center gap-2.5 bg-black/30 px-3.5 py-2">
              <span className="font-mono text-[11px] font-semibold text-white/70">
                16:48 / {meeting.recordingLength || '00:00'}
              </span>
              <div className="relative h-1 flex-1 rounded-full bg-white/15">
                <div className="absolute left-0 top-0 h-1 w-[35%] rounded-full bg-[#6366F1]" />
                <div className="absolute left-[35%] top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow" />
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.8} />
                <Settings className="h-3.5 w-3.5" strokeWidth={1.8} />
                <Volume2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                <Maximize className="h-3.5 w-3.5" strokeWidth={1.8} />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-[200px] items-center justify-center rounded-lg border border-[#EAECF0] bg-[#F9FAFB]">
            <div className="flex flex-col items-center gap-2 text-[#98A2B3]">
              <CameraOff className="h-6 w-6" strokeWidth={1.6} />
              <span className="text-[12px] font-medium">No recording for this call</span>
            </div>
          </div>
        )}
        <p className="py-3 text-[12px] text-[#667085]">
          {[meeting.title, meeting.dateLabel || formatMeetingDate(meeting.date), meeting.recordingLength, meeting.keyMoments?.length ? `${meeting.keyMoments.length} key moments` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {/* Keyword search — highlights matches across the AI Summary + Transcript below. */}
      <div className="px-6 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98A2B3]" strokeWidth={1.7} />
          <Input
            value={detailQuery}
            onChange={(event) => setDetailQuery(event.target.value)}
            placeholder="Search this call's summary & transcript…"
            className="h-9 w-full border-[#EAECF0] pl-9 text-[13px]"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="summary"
        onValueChange={(value) => onDetailTabChange(meeting.id, value)}
      >
        <TabsList className="h-auto w-full justify-start gap-0 rounded-none border-b border-[#EAECF0] bg-[#F9FAFB] p-0 px-6">
          <TabsTrigger value="summary" className={cn(tabTriggerClass, 'gap-1.5')}>
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            AI Summary
          </TabsTrigger>
          <TabsTrigger value="actions" className={tabTriggerClass}>
            Actions ({openCount} open)
          </TabsTrigger>
          {/* Notes tab temporarily hidden (SHOW_NOTES_TAB) — restore to re-enable. */}
          {SHOW_NOTES_TAB ? (
            <TabsTrigger value="notes" className={tabTriggerClass}>
              Notes
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="transcript" className={tabTriggerClass}>
            Transcript
          </TabsTrigger>
        </TabsList>

        {/* AI Summary — two columns (summary + key moments / actions) */}
        <TabsContent value="summary" className="mt-0 px-6 py-5">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <SummaryColumn meeting={meeting} query={detailQuery} />
            <ActionsColumn meeting={meeting} items={effectiveActions} handlers={actionHandlers} />
          </div>
        </TabsContent>

        <TabsContent value="actions" className="mt-0 px-6 py-5">
          <ActionsColumn meeting={meeting} items={effectiveActions} handlers={actionHandlers} />
        </TabsContent>

        {SHOW_NOTES_TAB ? (
          <TabsContent value="notes" className="mt-0 px-6 py-5">
            <NotesEditor meeting={meeting} clientId={clientId} />
          </TabsContent>
        ) : null}

        <TabsContent value="transcript" className="mt-0 px-6 py-5">
          {meeting.transcript?.length ? (
            <ul className="space-y-3">
              {meeting.transcript.map((line, index) => (
                <li key={index} className="flex gap-3">
                  <span className="w-12 flex-shrink-0 pt-0.5 font-mono text-[11px] text-[#98A2B3]">{line.time}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-[#6941C6]">{line.speaker}</div>
                    <div className="text-[13px] text-[#344054]">{highlightMatches(line.text, detailQuery)}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyDetail text="No transcript available for this call." />
          )}
        </TabsContent>
      </Tabs>

      {/* Ask Indie footer */}
      <div className="flex items-center justify-between gap-3 border-t border-[#E0E7FF] bg-[#EEF2FF] px-6 py-3.5">
        <div className="flex items-start gap-2.5">
          <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#4338CA]" strokeWidth={1.8} />
          <div>
            <div className="text-[13px] font-semibold text-[#4338CA]">Ask Indie about this call</div>
            <div className="mt-0.5 text-[11px] text-[#6366F1]">
              Opens with {meeting.dateLabel || formatMeetingDate(meeting.date)} transcript as context — ask about decisions made, next steps, or anything discussed
            </div>
          </div>
        </div>
        <Button
          className="h-8 flex-shrink-0 gap-1.5 bg-[#4338CA] text-[12px] text-white hover:bg-[#3730A3]"
          onClick={() => {
            openAskIndieForMeeting(meeting, 'meetings_detail')
            onAskIndie(meeting.id)
          }}
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
          Ask Indie
        </Button>
      </div>
    </div>
  )
}

function SummaryColumn({ meeting, query = '' }: { meeting: Meeting; query?: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-[#98A2B3]" strokeWidth={2} />
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#98A2B3]">Indie summary</span>
      </div>
      {meeting.summary ? (
        <p className="mt-3 text-[13px] leading-relaxed text-[#344054]">{highlightMatches(meeting.summary, query)}</p>
      ) : !meeting.summarySections?.length ? (
        <p className="mt-3 text-[13px] text-[#667085]">No summary captured for this call.</p>
      ) : null}

      {meeting.keyTakeaways?.length ? (
        <ul className="mt-3 space-y-1.5">
          {meeting.keyTakeaways.map((takeaway, index) => (
            <li key={index} className="flex gap-2 text-[13px] leading-relaxed text-[#344054]">
              <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#98A2B3]" />
              <span className="min-w-0">{renderBulletText(takeaway, query)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Full Grain AI summary — every thematic section in order. */}
      {meeting.summarySections?.length ? (
        <div className="mt-5 space-y-4">
          {meeting.summarySections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {section.title ? (
                <div className="text-[12px] font-bold text-[#1D2939]">
                  {highlightMatches(section.title, query)}
                </div>
              ) : null}
              {section.bullets?.length ? (
                <ul className="mt-1.5 space-y-1.5">
                  {section.bullets.map((bullet, bulletIndex) => (
                    <li
                      key={bulletIndex}
                      className="flex gap-2 text-[13px] leading-relaxed text-[#344054]"
                    >
                      <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#98A2B3]" />
                      <span className="min-w-0">{renderBulletText(bullet, query)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {meeting.keyMoments?.length ? (
        <>
          <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#667085]">
            Key moments — click to jump
          </div>
          <ul className="mt-2.5">
            {meeting.keyMoments.map((moment, index) => (
              <li
                key={index}
                className={cn(
                  'flex gap-2.5 py-2',
                  index < (meeting.keyMoments?.length || 0) - 1 && 'border-b border-[#F2F4F7]',
                )}
              >
                <button
                  type="button"
                  className="h-fit flex-shrink-0 rounded-lg border border-[#E9D7FE] bg-[#F4EBFF] px-1.5 py-0.5 font-mono text-[11px] font-semibold text-[#6941C6]"
                >
                  {moment.time}
                </button>
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold text-[#1D2939]">{highlightMatches(moment.title, query)}</div>
                  <div className="mt-0.5 text-[11px] leading-snug text-[#667085]">{highlightMatches(moment.quote, query)}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

function ActionsColumn({
  meeting,
  items,
  handlers,
}: {
  meeting: Meeting
  items?: MeetingActionItem[]
  handlers?: ActionHandlers
}) {
  // Fall back to the meeting's own items (e.g. the read-only Summary column before
  // handlers are threaded); interactive wiring is active only when handlers exist.
  const list = items ?? meeting.actionItems ?? []
  if (!list.length) {
    return <EmptyDetail text="No action items recorded." />
  }

  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#98A2B3]">Actions from this call</div>

      {/* Info banner */}
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-[#E0E7FF] bg-[#EEF2FF] px-3.5 py-2.5">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#4338CA]" strokeWidth={1.8} />
        <p className="text-[12px] leading-snug text-[#4338CA]">
          Accepted actions sync to your dashboard action list tagged{' '}
          <span className="font-medium">
            “From call · {meeting.dateLabel || formatMeetingDate(meeting.date)}”
          </span>
        </p>
      </div>

      {/* Action list */}
      <ul className="mt-3.5">
        {list.map((item, index) => (
          <ActionRow
            key={handlers ? handlers.keyFor(item, index) : index}
            item={item}
            actionKey={handlers ? handlers.keyFor(item, index) : String(index)}
            handlers={handlers}
            isLast={index === list.length - 1}
          />
        ))}
      </ul>
    </div>
  )
}

function ActionRow({
  item,
  actionKey,
  handlers,
  isLast,
}: {
  item: MeetingActionItem
  actionKey: string
  handlers?: ActionHandlers
  isLast: boolean
}) {
  const done = item.status === 'done'

  if (item.suggested) {
    return (
      <li className="mt-2 rounded-lg border border-dashed border-[#FEDF89] bg-[#FFFAEB] px-3.5 py-2.5">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 rounded-[3px] border-[1.5px] border-dashed border-[#F79009] bg-white" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-[#101828]">{item.text}</div>
            <div className="mt-1 text-[10px] text-[#DC6803]">✦ Suggested by Indie — not yet added</div>
          </div>
          {/* Temporarily hidden (SHOW_ACTION_ACCEPT_DISMISS) — logic retained for re-enable. */}
          {SHOW_ACTION_ACCEPT_DISMISS ? (
            <div className="flex flex-shrink-0 items-center gap-1.5">
              <button
                type="button"
                disabled={!handlers}
                onClick={() => handlers?.onAcceptAction(actionKey)}
                className="rounded-full border border-[#D1FADF] bg-[#ECFDF3] px-2.5 py-1 text-[11px] font-semibold text-[#027A48] hover:bg-[#D1FADF] disabled:opacity-50"
              >
                Add to dashboard
              </button>
              <button
                type="button"
                disabled={!handlers}
                onClick={() => handlers?.onDismissAction(actionKey)}
                className="rounded-full bg-[#F2F4F7] px-2.5 py-1 text-[11px] font-semibold text-[#667085] hover:bg-[#EAECF0] disabled:opacity-50"
              >
                Dismiss
              </button>
            </div>
          ) : null}
        </div>
      </li>
    )
  }

  return (
    <li className={cn('flex items-start gap-2.5 py-2.5', !isLast && 'border-b border-[#F2F4F7]')}>
      <button
        type="button"
        disabled={!handlers}
        onClick={() => handlers?.onToggleActionDone(actionKey, !done)}
        aria-pressed={done}
        aria-label={done ? 'Mark action not done' : 'Mark action done'}
        className="mt-0.5 flex-shrink-0 disabled:cursor-default"
      >
        {done ? (
          <span className="flex h-[15px] w-[15px] items-center justify-center rounded-[3px] bg-[#17B26A]">
            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          </span>
        ) : (
          <span className="block h-[15px] w-[15px] rounded-[3px] border-[1.5px] border-[#D0D5DD] bg-white hover:border-[#17B26A]" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className={cn('text-[13px]', done ? 'text-[#98A2B3] line-through' : 'text-[#101828]')}>
          {item.text}
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          {item.badge === 'done_in_dashboard' ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#E9D7FE] bg-[#F4EBFF] px-2 py-0.5 text-[10px] font-semibold text-[#53389E]">
              <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
              Done in dashboard
            </span>
          ) : item.badge === 'in_dashboard' ? (
            <span className="rounded-full border border-[#E9D7FE] bg-[#F4EBFF] px-2 py-0.5 text-[10px] font-semibold text-[#53389E]">
              In dashboard
            </span>
          ) : null}
        </div>
      </div>
    </li>
  )
}

function NotesEditor({ meeting, clientId }: { meeting: Meeting; clientId: string | number }) {
  const [body, setBody] = React.useState(meeting.notes || '')
  const [savedBody, setSavedBody] = React.useState(meeting.notes || '')
  const [saving, setSaving] = React.useState(false)
  const dirty = body !== savedBody

  // Re-seed when the expanded meeting (or its loaded note) changes.
  React.useEffect(() => {
    setBody(meeting.notes || '')
    setSavedBody(meeting.notes || '')
  }, [meeting.id, meeting.notes])

  const save = async () => {
    if (!clientId) return
    const next = body.trim()
    setSaving(true)
    await MeetingsService.saveNote(clientId, meeting.id, next)
    setSavedBody(next)
    setBody(next)
    setSaving(false)
  }

  return (
    <div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add your notes for this call…"
        className="min-h-[120px] w-full resize-y rounded-lg border border-[#EAECF0] bg-white px-3.5 py-2.5 text-[13px] leading-relaxed text-[#344054] focus:border-[#7F56D9] focus:outline-none focus:ring-1 focus:ring-[#7F56D9]"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] text-[#98A2B3]">
          {dirty ? 'Unsaved changes' : savedBody ? 'Saved' : 'Your notes are visible to your advisor'}
        </span>
        <Button
          onClick={save}
          disabled={!dirty || saving || !clientId}
          className="h-8 gap-1.5 bg-[#7F56D9] text-[12px] text-white hover:bg-[#6941C6] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save notes'}
        </Button>
      </div>
    </div>
  )
}

function EmptyDetail({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-[#EAECF0] p-4 text-[13px] text-[#667085]">
      <Sparkles className="h-4 w-4 text-[#98A2B3]" strokeWidth={1.6} />
      {text}
    </div>
  )
}
